import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return ""; }
}

// IPv4 → /16 subnet (matches first two octets) so a roaming user on the same
// home ISP doesn't trigger a false "new location" alarm, but a different city
// or country does. IPv6 / unknown → fall back to exact match.
function subnetKey(ip) {
  if (!ip) return "";
  const v4 = String(ip).match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
  return v4 ? `${v4[1]}.${v4[2]}` : String(ip);
}

function deviceIcon(device) {
  if (!device) return "💻";
  const d = device.toLowerCase();
  if (/mobile|android|iphone|ipad/.test(d)) return "📱";
  if (/bot|crawl|spider/.test(d)) return "🤖";
  if (/tablet/.test(d)) return "📟";
  return "💻";
}

const TOAST_FIRED_KEY = "omega.lastLogin.newLocationToast.v1";

/**
 * Tiny pill that displays the user's previous successful login.
 * Auto-fetches on mount, gracefully renders nothing if there's no history.
 * Tap to expand details (full timestamp, IP).
 * If the previous login came from a meaningfully different IP than the current
 * one, the pill turns amber/red, says "NEW LOCATION", and (once per session)
 * fires a security toast.
 */
export default function LastLoginBadge({ compact = false, style = {} }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/auth/login-history?limit=2`)
      .then(r => { if (!cancelled) { setData(r.data); setLoaded(true); } })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Detect new-location & fire a one-shot toast per session
  useEffect(() => {
    if (!data?.current || !data?.previous) return;
    const cur = data.current;
    const prev = data.previous;
    const curSubnet = subnetKey(cur.ip);
    const prevSubnet = subnetKey(prev.ip);
    if (!curSubnet || !prevSubnet || curSubnet === prevSubnet) return;
    // Different subnet → potential new location
    const fingerprint = `${cur.ts}|${curSubnet}|${prevSubnet}`;
    try {
      if (sessionStorage.getItem(TOAST_FIRED_KEY) === fingerprint) return;
      sessionStorage.setItem(TOAST_FIRED_KEY, fingerprint);
    } catch { /* ignore */ }
    toast.warning(`🔒 New sign-in location detected`, {
      description: `Previous: ${prev.ip || "unknown"} (${prev.device || "device"}). Current: ${cur.ip || "unknown"}. If this wasn't you, change your password immediately.`,
      duration: 14000,
    });
  }, [data]);

  if (!loaded || !data) return null;
  const prev = data.previous;
  if (!prev) return null;

  const cur = data.current;
  const isNewLocation = cur && prev && subnetKey(cur.ip) && subnetKey(prev.ip) && subnetKey(cur.ip) !== subnetKey(prev.ip);

  const bg = isNewLocation ? "rgba(251,191,36,0.12)" : "rgba(74,222,128,0.08)";
  const border = isNewLocation ? "rgba(251,191,36,0.45)" : "rgba(74,222,128,0.25)";
  const dotColor = isNewLocation ? "#fbbf24" : "#4ade80";
  const textColor = isNewLocation ? "rgba(255,238,200,0.95)" : "rgba(220,255,225,0.85)";

  return (
    <button
      type="button"
      data-testid="last-login-badge"
      data-new-location={isNewLocation ? "true" : "false"}
      onClick={() => setExpanded(e => !e)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: compact ? "4px 9px" : "5px 11px",
        borderRadius: 999,
        background: bg,
        border: `1px solid ${border}`,
        color: textColor,
        fontSize: compact ? 10 : 11,
        fontWeight: 600,
        cursor: "pointer",
        letterSpacing: 0.2,
        whiteSpace: "nowrap",
        ...style,
      }}
      title={isNewLocation ? "Different IP than your last sign-in — tap for details" : "Tap for full sign-in details"}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: dotColor, flexShrink: 0 }} />
      {isNewLocation && (
        <span data-testid="last-login-badge-newloc" style={{ color: "#fbbf24", fontWeight: 700, marginRight: 2 }}>
          NEW LOCATION ·
        </span>
      )}
      {expanded ? (
        <span data-testid="last-login-badge-detail">
          {deviceIcon(prev.device)} {new Date(prev.ts).toLocaleString()}
          {prev.device ? ` · ${prev.device}` : ""}
          {prev.ip ? ` · ${prev.ip}` : ""}
        </span>
      ) : (
        <span>
          {deviceIcon(prev.device)} Last sign-in {timeAgo(prev.ts)}
          {prev.device ? ` · ${prev.device}` : ""}
        </span>
      )}
    </button>
  );
}
